import { Modal, ModalContent, ModalOverlay } from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'

import { AddPreferredAgentForm } from './AddPreferredAgentForm'
import { FormData, useTeamMembership } from '../../../../hooks/useTeamMembership'
import { ExistingAgentCard } from './ExistingAgentCard'
import { ClientTeamMembership } from '../../../../types/clientTeamMembership'

interface AddPreferredAgentModalProps {
  clientTeamMembership?: ClientTeamMembership
  clientId: string
  isOpen: boolean
  isReplaceAction?: boolean
  onClose: () => void
  customerType?: 'RealEstateAgent' | 'LoanOfficer' | 'InsuranceAgent' | 'TitleAgent'
}

export interface CustomerProfileResponse {
  id: string
  firstName: string
  lastName: string
  email: string
  stateLicense: string | null
  photoUri: string
  corporateProfile: {
    name: string
    avatarUri: string | null
  }
}

export const AddPreferredAgentModal = ({
  isReplaceAction,
  isOpen,
  onClose,
  clientTeamMembership,
  clientId,
  customerType
}: AddPreferredAgentModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [existingProfileData, setExistingProfileData] = useState<CustomerProfileResponse | null>(null)
  const clientTeamMembershipId = clientTeamMembership?.id
  const {
    createMembership,
    createMembershipWithCustomer,
    replaceMembership,
    replaceMembershipWithCustomer,
    checkExistingAgent,
    handleError
  } = useTeamMembership({
    clientId,
    clientTeamMembershipId,
    onSuccess: onClose
  })
  const handleReset = () => {
    setExistingProfileData(null)
    onClose()
  }

  const handleSubmit = async (data: FormData) => {
    setIsSubmitting(true)

    try {
      const existingAgent = await checkExistingAgent(data.email)

      if (existingAgent) {
        setExistingProfileData(existingAgent)
        setIsSubmitting(false)
        return
      }

      // No existing agent found, replace with new one
      if (isReplaceAction) {
        await replaceMembership(data)
      } else {
        await createMembership(data)
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No existing agent found, proceed with creation
        if (isReplaceAction) {
          await replaceMembership(data)
        } else {
          await createMembership(data)
        }
      } else {
        handleError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleExistingConfirm = async () => {
    if (!existingProfileData) return

    try {
      setIsSubmitting(true)
      if (isReplaceAction) {
        await replaceMembershipWithCustomer(existingProfileData.id)
      } else {
        await createMembershipWithCustomer(existingProfileData.id)
      }
    } catch (error) {
      handleError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size='lg'>
        <ModalOverlay />
        <ModalContent>
          {existingProfileData ? (
            <ExistingAgentCard
              agent={existingProfileData}
              isSubmitting={isSubmitting}
              onClose={handleReset}
              onConfirm={handleExistingConfirm}
              customerType={customerType}
            />
          ) : (
            <AddPreferredAgentForm
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              isReplaceAction={isReplaceAction}
              onClose={onClose}
              customerType={customerType}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
